import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export type AdminUserStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'INACTIVE';

export interface AppUser extends User {
  role: string;
  status: AdminUserStatus;
  profile_name?: string;
  avatar_url?: string;
  profile_updated_at?: string | null;
  user_metadata: {
    full_name?: string;
    avatar_url?: string;
  };
}

const resolveAvatarUrl = (raw?: string | null) => {
  const value = (raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('avatars/')) {
    const { data } = supabase.storage.from('imgsinsecrj').getPublicUrl(value);
    return data.publicUrl;
  }
  return value;
};

const resolveUserStatus = (rawStatus: unknown, approvedFlag?: unknown): AdminUserStatus => {
  if (typeof rawStatus === 'string') {
    const normalized = rawStatus.toUpperCase();
    if (['PENDING', 'APPROVED', 'REJECTED', 'INACTIVE'].includes(normalized)) {
      return normalized as AdminUserStatus;
    }
  }

  if (approvedFlag === false) {
    return 'PENDING';
  }

  return 'APPROVED';
};

const fetchUsuarios = async (): Promise<AppUser[]> => {
  // 1) Buscar usuários via RPC
  const { data, error } = await supabase.rpc('get_all_users');
  if (error) {
    console.error('Erro ao buscar usuários via RPC:', error);
    throw new Error(error.message || 'Erro ao buscar usuários');
  }

  const rawUsers = (data || []) as any[];
  if (!rawUsers.length) return [];

  // 2) Buscar perfis correspondentes para obter nome e avatar_url
  const ids = rawUsers.map((u) => u.id).filter(Boolean);
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, name, avatar_url, status, updated_at')
    .in('id', ids);

  if (profilesError) {
    console.warn('Aviso: não foi possível buscar profiles:', profilesError.message);
  }

  const profileMap = new Map<string, any>();
  (profiles || []).forEach((p) => {
    profileMap.set(p.id, p);
  });

  // 3) Mesclar dados
  const merged = rawUsers.map((u) => {
    const profile = profileMap.get(u.id);
    
    // Prioriza o nome da tabela profiles, depois metadata, depois parte do email
    const fullName = profile?.name || u.user_metadata?.full_name || u.user_metadata?.name || u.email?.split('@')[0] || 'Usuário';
    
    // Resolve Avatar
    const avatarFromMetadata = u.user_metadata?.avatar_url || u.user_metadata?.avatarUrl;
    const avatarFromProfile = profile?.avatar_url || null;
    const finalAvatar = resolveAvatarUrl(avatarFromProfile || avatarFromMetadata || '');

    // Resolve Status e Role
    const status = resolveUserStatus(profile?.status || u.status, (u.user_metadata as Record<string, unknown>)?.approved);
    const role = u.role || (u.user_metadata as any)?.role || 'HOMOLOGACAO';

    // Captura o último acesso (tenta diversas variações que o Supabase/RPC podem retornar)
    const lastSignIn = u.last_sign_in_at || (u as any).last_login || (u as any).last_access || u.user_metadata?.last_sign_in_at;

    return {
      ...u,
      id: u.id,
      email: u.email,
      last_sign_in_at: lastSignIn,
      role,
      status,
      profile_name: fullName,
      avatar_url: finalAvatar,
      profile_updated_at: profile?.updated_at || null,
      user_metadata: {
        ...u.user_metadata,
        full_name: fullName,
        avatar_url: finalAvatar,
      },
    } as AppUser;
  });

  return merged;
};

export const useUsuarios = () => {
  return useQuery<AppUser[], Error>({
    queryKey: ['usuarios'],
    queryFn: fetchUsuarios,
  });
};
